import * as React from 'react';
import { Portal } from '@reach/portal';
import { Transition } from 'react-transition-group';
import FocusLock from 'react-focus-lock';
import { createContext, useId } from '@wartek-id/react-utils';
import clsx from 'clsx';
import { Primitive } from '@wartek-id/react-primitive';
import { Text } from '@wartek-id/text';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var _a;
var DialogContextProvider = (_a = createContext(), _a[0]), useDialogContext = _a[1];

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Dialog-module_dialog___0aCK{display:flex;width:100vw;height:100vh;position:fixed;left:0;top:0;z-index:1300;justify-content:center}.Dialog-module_dialogOverlay__ed7pb{z-index:100;background-color:rgba(0,0,0,.7);position:fixed;top:0;right:0;bottom:0;left:0}.Dialog-module_dialogContentContainer__y6r9N{display:flex;width:100vw;height:100vh;position:fixed;left:0;top:0;z-index:100;justify-content:center;align-items:flex-start;overflow:auto}.Dialog-module_dialogContent__phups{max-width:calc(100vw - 32px);width:360px;background-color:#fff;border-radius:var(--size-border-radius-base);display:flex;flex-direction:column;position:relative;outline:2px solid transparent;outline-offset:2px;color:inherit;margin-top:10rem;margin-bottom:3.75rem;z-index:100}.Dialog-module_dialogSubtitle__2N20T{margin-top:8px}.Dialog-module_dialogHeader__1aRui{padding:16px 20px;border-radius:var(--size-border-radius-base) var(--size-border-radius-base) 0 0}.Dialog-module_dialogHeader__1aRui.Dialog-module_compact__ly2Yz{background:var(--color-surface-subdued)}.Dialog-module_dialogHeader__1aRui.Dialog-module_with-subtitle__2c32n{background:var(--color-background-default)}.Dialog-module_dialogHeaderBordered__rS57o{border-bottom:1px solid var(--color-border-disabled)}.Dialog-module_dialogFooter__1tIta{display:flex;justify-content:flex-end;padding:16px 20px}.Dialog-module_dialogFooterBordered__2ehW4{border-top:1px solid var(--color-border-disabled)}.Dialog-module_noscroll__j5Pxt{overflow:hidden}.Dialog-module_dialogCloseButton__1qw8E{outline:none;background:none;display:flex;border:none;position:absolute;padding:0;right:20px;top:12px;cursor:pointer}.Dialog-module_dialogCloseButton__1qw8E:focus{box-shadow:0 0 0 2px var(--color-blue-50);border-radius:var(--size-border-radius-base)}";
var styles = {"dialog":"Dialog-module_dialog___0aCK","dialogOverlay":"Dialog-module_dialogOverlay__ed7pb","dialogContentContainer":"Dialog-module_dialogContentContainer__y6r9N","dialogContent":"Dialog-module_dialogContent__phups","dialogSubtitle":"Dialog-module_dialogSubtitle__2N20T","dialogHeader":"Dialog-module_dialogHeader__1aRui","compact":"Dialog-module_compact__ly2Yz","with-subtitle":"Dialog-module_with-subtitle__2c32n","dialogHeaderBordered":"Dialog-module_dialogHeaderBordered__rS57o","dialogFooter":"Dialog-module_dialogFooter__1tIta","dialogFooterBordered":"Dialog-module_dialogFooterBordered__2ehW4","noscroll":"Dialog-module_noscroll__j5Pxt","dialogCloseButton":"Dialog-module_dialogCloseButton__1qw8E","withSubtitle":"Dialog-module_with-subtitle__2c32n"};
styleInject(css_248z);

var sizesValue = {
    sm: '35rem',
    md: '45rem',
    lg: '60rem',
};
var DialogContent = function (_a) {
    var children = _a.children, transitionState = _a.transitionState, className = _a.className, props = __rest(_a, ["children", "transitionState", "className"]);
    var _b = useDialogContext(), _c = _b.size, size = _c === void 0 ? 'sm' : _c, customSize = _b.customSize, onClose = _b.onClose, componentId = _b.componentId, type = _b.type;
    var classes = clsx(styles.dialogContent, className);
    var handleKeyDown = function (event) {
        if (event.key === 'Escape') {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    };
    return (React.createElement("div", __assign({ className: classes, "data-state": transitionState, role: type === 'alert' ? 'alertdialog' : 'dialog', "aria-modal": "true", "aria-labelledby": "dialog-title-".concat(componentId), "aria-describedby": "dialog-description-".concat(componentId), style: {
            width: customSize || sizesValue[size],
        } }, props, { onKeyDown: handleKeyDown }),
        React.createElement("div", { id: "dialog-content" }, children)));
};

var DESCRIPTION_NAME$5 = 'DialogOverlay';
var DESCRIPTION_DEFAULT_TAG$3 = 'div';
var DialogOverlay = React.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? DESCRIPTION_DEFAULT_TAG$3 : _a, children = props.children, transitionState = props.transitionState, overlayProps = __rest(props, ["as", "children", "transitionState"]);
    return (React.createElement(Primitive, __assign({ as: as, "data-state": transitionState, "aria-hidden": true, className: styles.dialogOverlay }, overlayProps, { ref: ref }), children));
});
DialogOverlay.displayName = DESCRIPTION_NAME$5;

var DESCRIPTION_NAME$4 = 'DialogAction';
var DESCRIPTION_DEFAULT_TAG$2 = 'button';
var DialogAction = React.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? DESCRIPTION_DEFAULT_TAG$2 : _a, actionProps = __rest(props, ["as"]);
    return React.createElement(Primitive, __assign({ type: "button", ref: ref, as: as }, actionProps));
});
DialogAction.displayName = DESCRIPTION_NAME$4;

var DESCRIPTION_NAME$3 = 'DialogCancel';
var DESCRIPTION_DEFAULT_TAG$1 = 'button';
var DialogCancel = React.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? DESCRIPTION_DEFAULT_TAG$1 : _a, cancelProps = __rest(props, ["as"]);
    return React.createElement(Primitive, __assign({ type: "button", ref: ref, as: as }, cancelProps));
});
DialogCancel.displayName = DESCRIPTION_NAME$3;

var DESCRIPTION_NAME$2 = 'DialogHeader';
var titleSize = {
    'with-subtitle': 'heading-md',
    compact: 'heading-sm',
};
var DialogHeader = function (_a) {
    var children = _a.children, className = _a.className, title = _a.title, subtitle = _a.subtitle, borderless = _a.borderless, _b = _a.variant, variant = _b === void 0 ? 'with-subtitle' : _b, props = __rest(_a, ["children", "className", "title", "subtitle", "borderless", "variant"]);
    var componentId = useDialogContext().componentId;
    var classes = clsx(styles.dialogHeader, !borderless && styles.dialogHeaderBordered, styles[variant], className);
    return (React.createElement("div", __assign({ className: classes }, props), title || subtitle ? (React.createElement(React.Fragment, null,
        React.createElement(Text, { as: "h2", variant: titleSize[variant], id: "dialog-title-".concat(componentId) }, title),
        variant === 'with-subtitle' && subtitle && (React.createElement(Text, { variant: "body", className: styles.dialogSubtitle, color: borderless ? 'default' : 'subdued', id: "dialog-description-".concat(componentId) }, subtitle)))) : (children)));
};
DialogHeader.displayName = DESCRIPTION_NAME$2;

var DESCRIPTION_NAME$1 = 'DialogFooter';
var DialogFooter = function (_a) {
    var children = _a.children, className = _a.className, borderless = _a.borderless, props = __rest(_a, ["children", "className", "borderless"]);
    var classes = clsx(styles.dialogFooter, !borderless && styles.dialogFooterBordered, className);
    return (React.createElement("div", __assign({ className: classes }, props), children));
};
DialogFooter.displayName = DESCRIPTION_NAME$1;

var DESCRIPTION_NAME = 'DialogCloseButton';
var DESCRIPTION_DEFAULT_TAG = 'button';
var DialogCloseButton = React.forwardRef(function (props, ref) {
    var _a = props.as, as = _a === void 0 ? DESCRIPTION_DEFAULT_TAG : _a, buttonProps = __rest(props, ["as"]);
    var onClose = useDialogContext().onClose;
    return (React.createElement(Primitive, __assign({ "aria-label": "Close", ref: ref, as: as, className: styles.dialogCloseButton, onClick: function () { return onClose === null || onClose === void 0 ? void 0 : onClose(); } }, buttonProps),
        React.createElement("svg", { width: "28", height: "28", viewBox: "0 0 28 28", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M22.1666 7.47837L20.5216 5.83337L13.9999 12.355L7.47825 5.83337L5.83325 7.47837L12.3549 14L5.83325 20.5217L7.47825 22.1667L13.9999 15.645L20.5216 22.1667L22.1666 20.5217L15.6449 14L22.1666 7.47837Z", fill: "#45474A" }))));
});
DialogCloseButton.displayName = DESCRIPTION_NAME;

var Dialog = function (_a) {
    var children = _a.children, _b = _a.autoFocus, autoFocus = _b === void 0 ? true : _b, _c = _a.returnFocus, returnFocus = _c === void 0 ? true : _c, _d = _a.closeOnOverlayClick, closeOnOverlayClick = _d === void 0 ? true : _d, customSize = _a.customSize, props = __rest(_a, ["children", "autoFocus", "returnFocus", "closeOnOverlayClick", "customSize"]);
    var isOpen = props.isOpen, onClose = props.onClose, size = props.size, _e = props.type, type = _e === void 0 ? 'modal' : _e, _f = props.animationDuration, animationDuration = _f === void 0 ? { enter: 0, exit: 0 } : _f, dialogProps = __rest(props, ["isOpen", "onClose", "size", "type", "animationDuration"]);
    var helperId = useId();
    React.useEffect(function () {
        if (isOpen) {
            document.body.classList.add(styles.noscroll);
        }
        else {
            document.body.classList.remove(styles.noscroll);
        }
        return function cleanup() {
            document.body.classList.remove(styles.noscroll);
        };
    }, [isOpen]);
    // filter overlay component from children
    var overlay = React.Children.toArray(children).filter(function (child) {
        return child.type.displayName === 'DialogOverlay' ||
            child.type.displayName === 'SheetOverlay';
    })[0];
    var components = React.Children.toArray(children).filter(function (child) {
        return child.type.displayName !== 'DialogOverlay' &&
            child.type.displayName !== 'SheetOverlay';
    });
    var handleContainerClick = function (event) {
        var el = event.target;
        if (closeOnOverlayClick &&
            type === 'modal' &&
            el.classList.contains(styles.dialogContentContainer)) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    };
    return (React.createElement(DialogContextProvider, { value: {
            isOpen: isOpen,
            onClose: onClose,
            size: size,
            customSize: customSize,
            animationDuration: animationDuration,
            type: type,
            componentId: helperId,
        } },
        React.createElement(Portal, null,
            React.createElement(Transition, { appear: true, in: isOpen, timeout: {
                    enter: animationDuration.enter,
                    exit: animationDuration.exit,
                }, unmountOnExit: true }, function (state) {
                return (React.createElement(React.Fragment, null,
                    !!overlay ? React.cloneElement(overlay) : React.createElement(React.Fragment, null),
                    React.createElement(FocusLock, { autoFocus: autoFocus, returnFocus: returnFocus },
                        React.createElement("div", __assign({ className: styles.dialogContentContainer, onClick: handleContainerClick, tabIndex: -1 }, dialogProps), React.Children.map(components, function (child) {
                            return React.cloneElement(child, { transitionState: state });
                        })))));
            }))));
};

export { Dialog, DialogAction, DialogCancel, DialogCloseButton, DialogContent, DialogContextProvider, DialogFooter, DialogHeader, DialogOverlay, useDialogContext };
